
import { useState } from "react";
import { supabase, checkDuplicateTransaction } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { createTransactionHash } from "../utils/transactionHash";

// Use a global variable to track recently created transactions with timestamps
// This persists across component mounts/unmounts
const recentTransactions = new Map<string, number>();
// Track full objects for additional verification
const recentTransactionData = new Map<string, any>();
// Add a global semaphore to prevent parallel submissions
let isGlobalSubmissionInProgress = false;

export const useTransactionCreate = () => {
  const [isCreating, setIsCreating] = useState(false);
  const [lastCreatedHash, setLastCreatedHash] = useState<string | null>(null);
  
  const createTransaction = async (transactionData: any) => {
    // Add global lock protection
    if (isGlobalSubmissionInProgress) {
      console.log("Global submission lock is active, preventing duplicate");
      toast.warning("Processamento em andamento, aguarde um momento");
      return { 
        success: false, 
        message: "Uma transação já está em processo de criação" 
      };
    }

    // Add component-level lock
    if (isCreating) {
      toast.warning("Uma transação já está em processo de criação");
      return { 
        success: false, 
        message: "Uma transação já está em processo de criação" 
      };
    }
    
    try {
      // Set both local and global locks
      setIsCreating(true);
      isGlobalSubmissionInProgress = true;
      
      // Create hash for this transaction
      const transactionHash = createTransactionHash(transactionData);
      
      console.log("Created transaction hash:", transactionHash);
      console.log("Transaction data:", JSON.stringify(transactionData));
      
      // Check if we've recently created this exact transaction
      const now = Date.now();
      const recentCreation = recentTransactions.get(transactionHash);
      
      if (recentCreation && now - recentCreation < 60000) { // aumentado para 60 segundos
        console.log("Recent duplicate detected in memory cache");
        toast.warning("Esta transação parece ter sido registrada recentemente");
        return {
          success: false,
          message: "Esta transação já foi registrada nos últimos 60 segundos"
        };
      }
      
      // Additional check against stored full transaction data
      const storedTransactions = Array.from(recentTransactionData.values());
      const similarTransaction = storedTransactions.find(stored => {
        return (
          stored.type === transactionData.type &&
          stored.amount === transactionData.amount &&
          stored.customer_id === transactionData.customer_id &&
          stored.description === transactionData.description &&
          stored.payment_method === transactionData.payment_method &&
          stored.due_date === transactionData.due_date
        );
      });
      
      if (similarTransaction) {
        console.log("Similar transaction found in detailed memory cache");
        toast.warning("Uma transação muito similar já foi registrada recentemente");
        return {
          success: false,
          message: "Transação similar já registrada recentemente"
        };
      }
      
      // Check if this transaction hash matches our last created transaction
      if (lastCreatedHash === transactionHash) {
        console.log("Exact match with last created transaction hash");
        toast.warning("Esta transação idêntica já foi registrada");
        return { 
          success: false, 
          message: "Transação idêntica já registrada" 
        };
      }
      
      // Verificar duplicatas no lado do servidor com verificação adicional
      console.log("Checking for duplicates on server side");
      const isDuplicate = await checkDuplicateTransaction(transactionData);
      if (isDuplicate) {
        console.log("Server reported this as a duplicate transaction");
        toast.warning("Esta transação parece ter sido registrada recentemente");
        return { 
          success: false, 
          message: "Esta transação parece ter sido registrada recentemente. Por favor, verifique a lista de transações." 
        };
      }
      
      // Add a small delay to avoid race conditions with multiple submissions
      await new Promise(r => setTimeout(r, 800));
      
      // Se chegou até aqui, provavelmente não é uma duplicata
      console.log("Submitting transaction to database");
      const { data, error } = await supabase
        .from('transactions')
        .insert(transactionData)
        .select();

      if (error) {
        console.error("Error inserting transaction:", error);
        if (error.message && (
            error.message.includes("já está sendo processada") || 
            error.message.includes("foi enviada recentemente")
        )) {
          toast.warning("Esta transação parece ter sido enviada recentemente");
          return { 
            success: false, 
            message: "Esta transação parece ter sido enviada recentemente. Aguarde um momento antes de tentar novamente." 
          };
        }
        throw error;
      }
      
      // On success, track this transaction
      if (data && data.length > 0) {
        const completedTransaction = data[0];
        console.log("Transaction created successfully:", completedTransaction.id);
        
        // Record the transaction hash with current timestamp
        recentTransactions.set(transactionHash, now);
        recentTransactionData.set(transactionHash, transactionData);
        setLastCreatedHash(transactionHash);
        
        // Clean up old entries after 10 minutes
        setTimeout(() => {
          recentTransactions.delete(transactionHash);
          recentTransactionData.delete(transactionHash);
        }, 10 * 60 * 1000); // aumentado para 10 minutos
        
        // Dispatch an event to notify other components
        window.dispatchEvent(new CustomEvent('transaction-created', { 
          detail: { transaction: completedTransaction }
        }));
      }
      
      return { success: true, data };
    } catch (error) {
      console.error('Error creating transaction:', error);
      return { success: false, error };
    } finally {
      // Adiciona um pequeno atraso maior antes de permitir outra criação
      setTimeout(() => {
        setIsCreating(false);
        isGlobalSubmissionInProgress = false;
      }, 3000); // aumentado para 3 segundos
    }
  };

  return { createTransaction };
};
